import { Component, Inject, QueryList, ViewChildren } from '@angular/core';
import { AddressDTO, EmailDTO, NoteDTO, PersonDTO, PhoneNumberDTO } from '@iota/iota-management-portal-v4-client';
import { IotaModalOptions, IotaModalSize, MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@iota/iota-ui-components';
import { Store } from '@ngrx/store';
import { filter, take } from 'rxjs';
import {
    RemovePersonAddress,
    RemovePersonEmail,
    RemovePersonNote,
    RemovePersonPhoneNumber,
    UpsertPerson,
    UpsertPersonAddress,
    UpsertPersonEmail,
    UpsertPersonNote,
    UpsertPersonPhoneNumber
} from 'src/app/store/actions/person.actions';
import { personActionLoading, personById } from 'src/app/store/reducer/person.reducer';
import { ContactInformationTypes } from '../../constants-and-enums/contact-information-type';
import { deepCopy, DefaultAddress, DefaultEmail, DefaultPhoneNumber } from '../../constants-and-enums/default-objects';
import { GenderDescriptions, Genders } from '../../constants-and-enums/gender';
import { isEmptyOrNull, waitForBooleanValue } from '../../utils';
import { CompanyMode } from '../../constants-and-enums/company-mode.enum';
import { AddressComponent, AddressUpsertEvent } from '../../components/address/address.component';
import { EmailComponent } from '../../components/email/email.component';
import { PhoneNumberComponent } from '../../components/phone-number/phone-number.component';

interface DialogData {
    person: PersonDTO;
    mode: CompanyMode;
}

@Component({
    standalone: false,
    selector: 'app-dialog-person',
    templateUrl: './dialog-person.component.html',
    styleUrls: ['./dialog-person.component.scss']
})
export class DialogPersonComponent {
    @ViewChildren('addresses') addressesChildren: QueryList<AddressComponent>;
    @ViewChildren('emails') emailsChildren: QueryList<EmailComponent>;
    @ViewChildren('phoneNumbers') phoneNumbersChildren: QueryList<PhoneNumberComponent>;

    public modalOptions: Partial<IotaModalOptions> = {
        size: IotaModalSize.SIZE_7XL,
        showToggleFullScreenButton: true
    };

    genders = Genders;
    contactInformationTypes = ContactInformationTypes;

    updatedPerson: PersonDTO;

    personActionLoading$ = this.store.select(personActionLoading);
    mode: CompanyMode;

    constructor(
        public dialogRef: MatDialogRef<DialogPersonComponent>,
        private dialog: MatDialog,
        @Inject(MAT_DIALOG_DATA) private data: DialogData,
        private store: Store
    ) {
        dialogRef.disableClose = true;
        this.updatedPerson = deepCopy(this.data.person);
        this.mode = this.data.mode;
    }

    get isCreation() {
        return isEmptyOrNull(this.updatedPerson.personId);
    }

    onCancelClick(): void {
        this.dialogRef.close();
    }

    onSaveClick() {
        if (this.isCreation) {
            this.updatedPerson.addresses = this.addressesChildren
                .toArray()
                .map((a) => a.getUpdatedAddress())
                .filter((a) => !!a);

            this.updatedPerson.emails = this.emailsChildren
                .toArray()
                .map((e) => e.getUpdatedEmail())
                .filter((e) => !!e);

            this.updatedPerson.phoneNumbers = this.phoneNumbersChildren
                .toArray()
                .map((p) => p.getUpdatedPhoneNumber())
                .filter((p) => !!p);
        }

        this.store.dispatch(UpsertPerson({ person: this.updatedPerson, mode: this.mode }));

        this.store
            .select(personActionLoading)
            .pipe(
                filter((loading) => !loading),
                take(1)
            )
            .subscribe(() => this.dialogRef.close({ reload: true }));
    }

    getGenderDescription(gender: string) {
        return GenderDescriptions.get(gender);
    }

    /* Note */
    addNote(note: NoteDTO) {
        this.updatedPerson.notes = [...this.updatedPerson.notes, note];

        if (!this.isCreation) {
            this.store.dispatch(UpsertPersonNote({ note, personId: this.updatedPerson.personId }));

            this.reloadNotesAfterActionLoadingFinished();
        }
    }

    deleteNote(note: NoteDTO) {
        this.updatedPerson.notes = this.updatedPerson.notes.filter((n) => n !== note);

        if (!this.isCreation && note.noteId) {
            this.store.dispatch(RemovePersonNote({ personId: this.updatedPerson.personId, noteId: note.noteId }));

            this.reloadNotesAfterActionLoadingFinished();
        }
    }

    async reloadNotesAfterActionLoadingFinished() {
        await waitForBooleanValue(this.store, personActionLoading, false);

        this.store
            .select(personById(this.updatedPerson.personId))
            .pipe(take(1))
            .subscribe((person) => {
                this.updatedPerson.notes = deepCopy(person.notes);
            });
    }

    /* Email */
    addEmail() {
        const newEmail: EmailDTO = DefaultEmail();
        this.updatedPerson.emails.unshift(newEmail);
    }

    saveEmail(updatedEmail: EmailDTO) {
        if (this.updatedPerson.personId) {
            this.store.dispatch(UpsertPersonEmail({ email: updatedEmail, personId: this.updatedPerson.personId }));
        }
    }

    cancelEmailEditing(email: EmailDTO) {
        if (!email.emailId) {
            this.updatedPerson.emails = this.updatedPerson.emails.filter((e) => e != email);
        }
    }

    deleteEmail(originalEmail: EmailDTO, newEmail: EmailDTO) {
        if (newEmail.emailId && this.updatedPerson.personId) {
            this.store.dispatch(RemovePersonEmail({ emailId: newEmail.emailId, personId: this.updatedPerson.personId }));
        }

        this.updatedPerson.emails = this.updatedPerson.emails.filter((e) => e != originalEmail);
    }

    /* PhoneNumber */
    addPhoneNumber() {
        const newPhoneNumber: PhoneNumberDTO = DefaultPhoneNumber();
        this.updatedPerson.phoneNumbers.unshift(newPhoneNumber);
    }

    savePhoneNumber(updatedPhoneNumber: PhoneNumberDTO) {
        if (this.updatedPerson.personId) {
            this.store.dispatch(
                UpsertPersonPhoneNumber({
                    phoneNumber: updatedPhoneNumber,
                    personId: this.updatedPerson.personId
                })
            );
        }
    }

    cancelPhoneNumberEditing(phoneNumber: PhoneNumberDTO) {
        if (!phoneNumber.phoneNumberId) {
            this.updatedPerson.phoneNumbers = this.updatedPerson.phoneNumbers.filter((e) => e != phoneNumber);
        }
    }

    deletePhoneNumber(originalPhoneNumber: PhoneNumberDTO, newPhoneNumber: PhoneNumberDTO) {
        if (newPhoneNumber.phoneNumberId && this.updatedPerson.personId) {
            this.store.dispatch(
                RemovePersonPhoneNumber({ phoneNumberId: newPhoneNumber.phoneNumberId, personId: this.updatedPerson.personId })
            );
        }

        this.updatedPerson.phoneNumbers = this.updatedPerson.phoneNumbers.filter((e) => e != originalPhoneNumber);
    }

    /* Address */
    addAddress() {
        const address = DefaultAddress();
        this.updatedPerson.addresses = [address].concat(this.updatedPerson.addresses);
    }

    async saveAddress(event: AddressUpsertEvent) {
        if (!this.isCreation) {
            this.store.dispatch(UpsertPersonAddress({ address: event?.address, personId: this.updatedPerson.personId }));

            await waitForBooleanValue(this.store, personActionLoading, false);

            this.store
                .select(personById(this.updatedPerson.personId))
                .pipe(take(1))
                .subscribe((person) => {
                    this.updatedPerson.addresses = deepCopy(person.addresses);
                });
        }
    }

    cancelAddressEditing(address: AddressDTO) {
        if (!address.addressId) {
            this.updatedPerson.addresses = this.updatedPerson.addresses.filter((a) => a != address);
        } else {
            const index = this.updatedPerson.addresses.indexOf(address);

            if (index !== -1) {
                this.updatedPerson.addresses[index] = deepCopy(this.data.person.addresses.find((a) => a.addressId === address.addressId));
            }
        }
    }

    deleteAddress(address: AddressDTO) {
        if (this.isCreation) {
            this.updatedPerson.addresses = this.updatedPerson.addresses.filter((add) => add !== address);
        } else {
            if (address.addressId) {
                this.store.dispatch(
                    RemovePersonAddress({
                        addressId: address.addressId,
                        personId: this.updatedPerson.personId
                    })
                );
            }

            this.updatedPerson.addresses = this.updatedPerson.addresses.filter((a) => a != address);
        }
    }
}
